import { Avatar, BottomSheet, Button, EmptyState, ErrorState, formatDate, Icon, Skeleton, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isApiError } from "../../api/client";
import { patchConversation, sendTemplate, type Conversation, type InboxTemplate } from "../../api/inbox";
import { handOverSentinel, useSheetBack } from "../../app/mobile/useSheetBack";
import { useAppContext } from "../../app/permissions";
import { useServices } from "../../app/services";
import { today as localDate } from "../../lib/dates";
import { firstName } from "./format";
import { useAssignees, useNumbers, useTemplates } from "./hooks";
import { inbox as t } from "./strings";
import s from "./inbox.module.css";

/* ------------------------------------------------------------------ info */

export function InfoSheet({ open, onOpenChange, c, lastWrote, canManage, phoneUnmasked, telHref, onArchived }: {
  open: boolean; onOpenChange: (o: boolean) => void; c: Conversation; lastWrote: number | null; canManage: boolean; phoneUnmasked: boolean; telHref: string | null; onArchived: () => void;
}) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const ctx = useAppContext();
  useSheetBack(open, () => onOpenChange(false));
  const name = c.name ?? t.newContact;
  const leave = () => { handOverSentinel(); onOpenChange(false); };
  const patch = useMutation({
    mutationFn: (v: { archived?: boolean; read?: boolean }) => patchConversation(client, c.id, v),
    onSuccess: async (_r, v) => {
      await qc.invalidateQueries({ queryKey: ["inbox"] });
      onOpenChange(false);
      if (v.archived !== undefined) { toast.show(v.archived ? t.info.archived : t.info.unarchived); if (v.archived) onArchived(); }
    },
    onError: () => toast.show(t.info.updateFailed),
  });
  const linkedMember = c.kind === "member" && c.memberId;
  const linkedLead = (c.kind === "lead" || c.kind === "unknown") && c.leadId;
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={name} description={t.thread.typeLabel[c.kind]}>
      <div className={s.who}><Avatar id={c.id} name={name} size="md" /><div><b>{name}</b><small>{phoneUnmasked ? c.phone : c.phone}</small></div></div>
      <div className={s.facts}>
        <div><small>{t.info.type}</small><b>{t.thread.typeLabel[c.kind]}</b></div>
        <div><small>{t.info.assigned}</small><b>{c.assignee?.name ?? t.assignee.none}</b></div>
        <div><small>{t.info.plan}</small><b>{c.record?.label ?? "—"}</b></div>
        <div><small>{t.info.lastWrote}</small><b>{lastWrote !== null ? formatDate(localDate(lastWrote, ctx.timeZone), ctx.locale, "weekday") : t.info.never}</b></div>
      </div>
      <div className={s.sacts}>
        {telHref ? <Button as="a" href={telHref}><Icon name="phone" />{t.info.call}</Button> : null}
        {linkedMember ? <Button as={Link} to={`/members/${c.memberId}`} replace onClick={leave}>{t.info.openMember}</Button> : null}
        {linkedLead ? <Button as={Link} to={`/leads/${c.leadId}`} replace onClick={leave}>{t.info.openLead}</Button> : null}
        {linkedMember && ctx.has("billing.create_bill") ? <Button variant="primary" as={Link} to={`/billing?collect=${c.memberId}`} replace onClick={leave}><Icon name="cash" />{t.info.collect}</Button> : null}
        {canManage ? <Button onClick={() => patch.mutate({ read: false })} disabled={patch.isPending || c.unreadCount > 0}><Icon name="mail" />{t.info.markUnread}</Button> : null}
        {canManage ? <Button onClick={() => patch.mutate({ archived: !c.archived })} disabled={patch.isPending}><Icon name="archive" />{c.archived ? t.info.unarchive : t.info.archive}</Button> : null}
      </div>
    </BottomSheet>
  );
}

/* ------------------------------------------------------------------ assign */

export function AssignSheet({ open, onOpenChange, c }: { open: boolean; onOpenChange: (o: boolean) => void; c: Conversation }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  useSheetBack(open, () => onOpenChange(false));
  const q = useAssignees(open, c.id);
  const assign = useMutation({
    mutationFn: (to: { id: string; name: string } | null) => patchConversation(client, c.id, { assigneeStaffId: to?.id ?? null }),
    onSuccess: async (_r, to) => { await qc.invalidateQueries({ queryKey: ["inbox"] }); onOpenChange(false); toast.show(to ? t.assign.done(to.name) : t.assign.unassign); },
    onError: () => toast.show(t.assign.failed),
  });
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={t.assign.title} description={t.assign.sub}>
      {q.isPending ? <div aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className={s.opt}><Skeleton width="50%" height={14} /></div>)}</div> : q.error ? (
        <ErrorState compact title={t.assign.error} message={isApiError(q.error) ? q.error.message : ""} onRetry={() => void q.refetch()} />
      ) : (
        <div role="listbox" aria-label={t.assign.title} className={s.lst}>
          <button type="button" role="option" aria-selected={!c.assignee} className={s.opt} onClick={() => assign.mutate(null)} disabled={assign.isPending}>
            <span className={s.optText}><b>{t.assign.unassign}</b></span>{!c.assignee ? <Icon name="check" className={s.ck} /> : null}
          </button>
          {(q.data ?? []).map((a) => (
            <button key={a.id} type="button" role="option" aria-selected={c.assignee?.id === a.id} className={s.opt} onClick={() => assign.mutate(a)} disabled={assign.isPending}>
              <Avatar id={a.id} name={a.name} size="md" /><span className={s.optText}><b>{a.name}</b></span>{c.assignee?.id === a.id ? <Icon name="check" className={s.ck} /> : null}
            </button>
          ))}
        </div>
      )}
    </BottomSheet>
  );
}

/* ------------------------------------------------------------------ templates */

/** Fill `{{param}}`; an unknown value stays visible as `{{param}}` so a gap can never go out looking finished. */
export const fillTemplate = (body: string, values: Record<string, string>) => body.replace(/\{\{(\w+)\}\}/g, (m, k: string) => (values[k] ? values[k] : m));

/** What the contact record already tells us. */
export function knownParams(tpl: InboxTemplate, c: Conversation): Record<string, string> {
  const known: Record<string, string> = {};
  const name = firstName(c.name);
  if (name) { known.name = name; known.recipientName = name; }
  if (c.record?.label) { known.plan = c.record.label; known.stage = c.record.label; }
  return Object.fromEntries(tpl.params.filter((p) => known[p]).map((p) => [p, known[p]!]));
}

/** The server fills these from the conversation's brand; the composer never asks, and sending them would override it. */
const SERVER_FILLED = new Set(["gym"]);

export function TemplateFlow({ open, onOpenChange, c, intentKey, onSent }: { open: boolean; onOpenChange: (o: boolean) => void; c: Conversation; intentKey: (sig: string) => string; onSent: () => void }) {
  const [picked, setPicked] = useState<InboxTemplate | null>(null);
  useEffect(() => { if (!open) setPicked(null); }, [open]);
  return (
    <>
      <TemplateList open={open && !picked} onOpenChange={onOpenChange} onPick={setPicked} brandId={c.brandId} />
      <TemplatePreview tpl={picked} c={c} onClose={() => setPicked(null)} onDone={() => { setPicked(null); onOpenChange(false); onSent(); }} intentKey={intentKey} />
    </>
  );
}

function TemplateList({ open, onOpenChange, onPick, brandId }: { open: boolean; onOpenChange: (o: boolean) => void; onPick: (t: InboxTemplate) => void; brandId: string }) {
  useSheetBack(open, () => onOpenChange(false));
  const q = useTemplates(open, brandId);
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={t.templates.title} description={t.templates.sub}>
      {q.isPending ? <div aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className={s.opt}><Skeleton width="60%" height={14} /></div>)}</div> : q.error ? (
        <ErrorState compact title={t.templates.error} message={isApiError(q.error) ? q.error.message : ""} onRetry={() => void q.refetch()} />
      ) : (q.data ?? []).length === 0 ? <EmptyState compact icon="file" title={t.templates.empty} description={t.templates.emptyBody} /> : (
        <div className={s.lst}>
          {(q.data ?? []).map((x) => (
            <button key={x.key} type="button" className={s.opt} onClick={() => onPick(x)}>
              <span className={s.optText}><b>{x.name}</b><small className={s.tpv}>{x.body}</small></span><Icon name="chevR" size={16} />
            </button>
          ))}
        </div>
      )}
    </BottomSheet>
  );
}

function TemplatePreview({ tpl, c, onClose, onDone, intentKey }: { tpl: InboxTemplate | null; c: Conversation; onClose: () => void; onDone: () => void; intentKey: (sig: string) => string }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const ctx = useAppContext();
  const numbers = useNumbers();
  const [typed, setTyped] = useState<Record<string, string>>({});
  const last = useState<{ v: InboxTemplate | null }>({ v: null })[0];
  if (tpl) last.v = tpl;
  const cur = tpl ?? last.v;
  useEffect(() => { setTyped({}); }, [tpl?.key]);
  useSheetBack(!!tpl, onClose);
  const send = useMutation({
    mutationFn: ({ params, key }: { params: Record<string, string>; key: string }) => sendTemplate(client, c.id, { templateKey: cur!.key, params }, key),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["inbox"] }); toast.show(t.templates.sent); onDone(); },
    onError: () => toast.show(t.templates.failed),
  });
  const known = cur ? knownParams(cur, c) : {};
  const gym = numbers.data?.find((n) => n.brandId === c.brandId)?.brandName ?? ctx.me?.business.name;
  const values: Record<string, string> = { ...known, ...typed };
  const asked = cur ? cur.params.filter((p) => !SERVER_FILLED.has(p)) : [];
  const missing = asked.filter((p) => !values[p]?.trim());
  const params = Object.fromEntries(asked.map((p) => [p, values[p]?.trim() ?? ""]));
  return (
    <BottomSheet open={!!tpl} onOpenChange={(o) => { if (!o) onClose(); }} title={cur?.name ?? ""} description={t.templates.preview}
      footer={cur ? <><Button onClick={onClose}>Back</Button><Button variant="primary" busy={send.isPending} busyLabel={t.templates.sending} disabled={missing.length > 0 || send.isPending} onClick={() => send.mutate({ params, key: intentKey(`tpl:${c.id}:${cur.key}:${JSON.stringify(params)}`) })}><Icon name="send" />{t.templates.send}</Button></> : undefined}>
      {cur ? (
        <>
          <div className={s.tplp}>
            <div className={`${s.msg} ${s.out} ${s.tpl}`}><span className={s.tph}><Icon name="file" size={14} />{t.thread.template} · {cur.name}</span><span className={s.tpb} data-testid="template-preview">{fillTemplate(cur.body, gym ? { ...values, gym } : values)}</span></div>
          </div>
          {asked.filter((p) => !known[p]).map((p) => (
            <label key={p} className={s.field}>
              <span>{t.templates.fill(p)}</span>
              <input value={typed[p] ?? ""} onChange={(e) => setTyped((x) => ({ ...x, [p]: e.target.value }))} autoComplete="off" />
            </label>
          ))}
        </>
      ) : null}
    </BottomSheet>
  );
}
